/**
 * FinanceInvoiceView - the Finance Invoice tab (10/11/26): the invoice
 * ledger. Every invoice is its own row (number, date, due date, amount,
 * project - and through it the customer and quote #), with its payments, so
 * the tab shows what was billed, what was collected and what is still owed:
 *
 *   Invoices              the list: search, filters, CSV, add / edit / void,
 *                         record a payment
 *   Receivables & aging   open balances per customer, by days past due
 *   Payments              money received over a range ($ collected)
 *
 * The ledger is the ONE record of invoicing on the main tab: a project's
 * Invoiced, Invoice #'s, Latest Invoice Date and Leftover are worked out of
 * it on the server, and the record editor's "Add an invoice" writes the same
 * rows. The 'finance-invoices' area (src/data/roles.js): Operations by role,
 * anyone else given it on the Team page; View reads, Edit changes.
 * Deep links: #/finance-invoices?project=<id>, ?invoice=<id>,
 * ?view=receivables | payments.
 */
import { useCallback, useEffect, useState } from 'react';
import { canEdit, canSeeTab } from '../data/roles';
import { useMyAccess } from '../hooks/useMyAccess';
import { useApp } from '../context/AppContext';
import { parseHashFilters } from '../utils/hashParams';
import { latestReads, useLiveRefresh } from '../live/useLive';
import { SegmentedControl } from '../components/ui/SegmentedControl';
import InvoicesPanel from '../components/financeInvoices/InvoicesPanel';
import ReceivablesPanel from '../components/financeInvoices/ReceivablesPanel';
import PaymentsPanel from '../components/financeInvoices/PaymentsPanel';
import InvoiceModal from '../components/financeInvoices/InvoiceModal';
import { errorOf, listInvoices, listProjects } from '../components/financeInvoices/financeInvoicesApi';

const VIEWS = [
  { value: 'invoices', label: 'Invoices' },
  { value: 'receivables', label: 'Receivables & aging' },
  { value: 'payments', label: 'Payments' },
];

function fromHash() {
  const h = parseHashFilters();
  return {
    view: VIEWS.some((v) => v.value === h.view) ? h.view : 'invoices',
    project: h.project || null,
    invoice: h.invoice || null,
  };
}

export default function FinanceInvoiceView() {
  const access = useMyAccess();
  const { fetchAll } = useApp() || {};
  const editable = canEdit(access, 'finance-invoices');
  const [place, setPlace] = useState(fromHash);
  const [open, setOpen] = useState(() => fromHash().invoice); // an invoice id, 'new', or null
  const [customer, setCustomer] = useState('');
  const [includeVoid, setIncludeVoid] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [projects, setProjects] = useState([]);
  const [tick, setTick] = useState(0);
  const [reads] = useState(latestReads);

  const load = useCallback(async () => {
    const t = reads.start();
    const res = await listInvoices({ include_void: includeVoid });
    if (!reads.show(t)) return;
    if (!res.ok) {
      setError(errorOf(res, 'Could not load the invoices.'));
      return;
    }
    setError('');
    setData(res.body);
  }, [includeVoid, reads]);
  const loadProjects = useCallback(async () => {
    const res = await listProjects();
    if (res.ok && Array.isArray(res.body)) setProjects(res.body);
  }, []);
  useEffect(() => {
    load();
  }, [load, tick]);
  useEffect(() => {
    loadProjects();
  }, [loadProjects]);
  // someone else's invoice or payment, or a project's invoicing changed from its editor
  useLiveRefresh(['finance-invoices', 'projects'], load, {
    match: (e) =>
      e.kind === 'finance-invoices' ||
      e.sub === 'invoices' ||
      (e.kind === 'projects' &&
        !e.sub &&
        (e.method === 'DELETE' ||
          (e.fields || []).some((f) =>
            ['invoiced_already', 'customer', 'name', 'quote_letter', 'po_number', 'status', 'terms'].includes(f)
          ))),
  });

  useEffect(() => {
    const onHash = () => {
      if (!/^#\/finance-invoices\b/.test(window.location.hash)) return;
      const h = fromHash();
      setPlace(h);
      if (h.invoice) setOpen(h.invoice);
    };
    window.addEventListener('hashchange', onHash);
    window.addEventListener('popstate', onHash);
    return () => {
      window.removeEventListener('hashchange', onHash);
      window.removeEventListener('popstate', onHash);
    };
  }, []);

  // keep the URL on the place shown, so a link or a reload comes back to it
  const go = useCallback((next) => {
    setPlace((cur) => {
      const p = { ...cur, ...next };
      const usp = new URLSearchParams();
      if (p.view !== 'invoices') usp.set('view', p.view);
      if (p.view === 'invoices' && p.project) usp.set('project', p.project);
      const qs = usp.toString();
      const hash = `#/finance-invoices${qs ? `?${qs}` : ''}`;
      if (window.location.hash !== hash)
        window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}${hash}`);
      return p;
    });
  }, []);

  // this tab's own saves: the lists here re-read, and so do the app's
  // projects (the live stream does not echo a tab's own writes back to it)
  const changed = useCallback(() => {
    setTick((t) => t + 1);
    loadProjects();
    if (typeof fetchAll === 'function') fetchAll();
  }, [fetchAll, loadProjects]);

  if (!canSeeTab(access, 'finance-invoices')) return null;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <SegmentedControl options={VIEWS} value={place.view} onChange={(v) => go({ view: v })} />
        <span style={{ fontSize: 11, color: '#6B7280' }}>
          {editable
            ? 'Each invoice is its own row with its payments. Projects’ Invoiced, Invoice #’s and Leftover follow this list.'
            : 'View only - changing invoices and payments needs Finance Invoice Edit.'}
        </span>
      </div>
      {place.view === 'invoices' && (
        <InvoicesPanel
          data={data}
          error={error}
          editable={editable}
          projects={projects}
          project={place.project}
          onProject={(id) => go({ project: id })}
          customer={customer}
          onCustomer={setCustomer}
          includeVoid={includeVoid}
          onIncludeVoid={setIncludeVoid}
          onOpen={(id) => setOpen(String(id))}
          onNew={() => setOpen('new')}
        />
      )}
      {place.view === 'receivables' && (
        <ReceivablesPanel
          tick={tick}
          onCustomer={(c) => {
            setCustomer(c);
            go({ view: 'invoices', project: null });
          }}
        />
      )}
      {place.view === 'payments' && <PaymentsPanel tick={tick} onOpen={(id) => setOpen(String(id))} />}
      {open && (
        <InvoiceModal
          key={open}
          invoiceId={open === 'new' ? null : open}
          projectId={open === 'new' ? place.project : null}
          projects={projects}
          editable={editable}
          onClose={() => setOpen(null)}
          onChanged={changed}
        />
      )}
    </div>
  );
}
